import { useEffect, useState } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Menu, X, MonitorPlay } from 'lucide-react';
import { CHAPTERS } from '../data/content';
import { scrollTo } from '../lib/scroll';

const NAV = CHAPTERS.filter((c) => c.id !== 'auftrag');

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    const ids = CHAPTERS.map((c) => c.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setActive(e.target.id));
      },
      { rootMargin: '-35% 0px -55% 0px' }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    scrollTo(`#${id}`);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color] duration-300 ${
        scrolled ? 'border-[#282C37]/80 bg-[#0F1012]/85' : 'border-transparent bg-transparent'
      }`}
    >
      <motion.div className="absolute inset-x-0 top-0 h-[2px] origin-left bg-[#FF6B00]" style={{ scaleX: progress }} data-testid="nav-progress" />
      <div className="wrap flex items-center justify-between py-3.5">
        <a href="#start" onClick={(e) => go(e, 'start')} data-testid="nav-logo" className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FF6B00]">
            <MonitorPlay className="h-5 w-5 text-white" />
          </span>
          <span className="font-display leading-tight">
            <span className="block text-sm font-extrabold tracking-tight">GLOBUS Koblenz</span>
            <span className="block font-mono-num text-[10px] font-medium uppercase tracking-[0.22em] text-[#7D8391]">Instore Media</span>
          </span>
        </a>

        <nav className="hidden items-center gap-1 lg:flex" data-testid="nav-links" aria-label="Kapitel">
          {NAV.map((c) => {
            const isActive = active === c.id;
            return (
              <a
                key={c.id}
                href={`#${c.id}`}
                onClick={(e) => go(e, c.id)}
                data-testid={`nav-link-${c.id}`}
                aria-current={isActive ? 'true' : undefined}
                className={`group relative flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive ? 'text-white' : 'text-[#B6BCC8] hover:text-white'
                }`}
              >
                <span className={`font-mono-num text-[10px] transition-colors duration-200 ${isActive ? 'text-[#FF6B00]' : 'text-[#5B606C] group-hover:text-[#FF6B00]'}`}>{c.n}</span>
                {c.label}
                {isActive && <motion.span layoutId="nav-active" className="absolute inset-0 -z-10 rounded-full bg-[#1E222A]" transition={{ type: 'spring', stiffness: 400, damping: 32 }} />}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <a href="#auftrag" onClick={(e) => go(e, 'auftrag')} data-testid="nav-cta-button" className="btn-primary hidden !px-5 !py-2.5 sm:inline-flex">
            Auftrag starten
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="rounded-lg border border-[#282C37] p-2 text-white transition-colors hover:border-[#FF6B00] lg:hidden"
            aria-label={open ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-[#282C37] bg-[#0F1012] lg:hidden" data-testid="nav-mobile-menu">
          <div className="wrap grid grid-cols-2 gap-1 py-4">
            {CHAPTERS.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                onClick={(e) => go(e, c.id)}
                data-testid={`nav-mobile-link-${c.id}`}
                className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-[#B6BCC8] transition-colors hover:bg-[#16181D] hover:text-white"
              >
                <span className="font-mono-num text-[10px] text-[#FF6B00]">{c.n}</span>
                {c.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
